<script lang="ts" setup>
  import { h } from "vue";
  import { useHistoryStore } from "~/stores/HistoryStore";

  const historyStore = useHistoryStore();

  interface DataTableHeader {
    label: string;
    key: string;
    hideTitleSp?: boolean;
    hideTitlePc?: boolean;
    sortable?: boolean;
    filterable?: boolean;
    align?: string;
    width: number;
    spAlign?: string;
    spWidth?: number;
    clickable?: boolean;
    render: (item: any) => any;
  }

  interface Props {
    headers: DataTableHeader[];
    items: any[];
    spRows: { key: string }[][];
    showDetail?: boolean;
    hoverable?: boolean;
    selectedFilters?: string[];
    spDefaultRows?: number;
    defaultSort?: "asc" | "desc";
    defaultSortItem?: string;
    rowCursor?: "pointer" | "default";
  }

  const localProps = defineProps<Props>();
  const emit = defineEmits<{
    (event: "onClickHeader", type: string, key: string): void;
    (event: "onClickItem", item: any): void;
  }>();

  const getSpHeaders = (rows: { key: string; sort?: number }[]) => {
    return localProps.headers
      .map(header => {
        const row = rows.find(row => row.key === header.key) || {};
        return { ...header, ...row };
      })
      .sort((a: any, b: any) => (a.sort || 0) - (b.sort || 0))
      .filter(header => rows.map(row => row.key).includes(header.key));
  };

  const getSpRows = () => {
    if (localProps.showDetail) {
      return localProps.spRows;
    } else {
      return localProps.spRows.slice(0, localProps.spDefaultRows || 1);
    }
  };

  const isSp = isSpDisplayRef();
  const sort = computed(() => useRoute().query.sort || localProps.defaultSort || "asc");
  const sortItem = computed(() => useRoute().query.sort_item || localProps.defaultSortItem || "");

  const getSortActiveColor = (header: DataTableHeader, direction: string) => {
    if (sortItem.value === header.key && sort.value === direction) {
      return "blue";
    }
    return "";
  };

  const handleClickHeader = (item: DataTableHeader) => {
    if (item.sortable) {
      handleSort(item);
    }
    if (item.filterable) {
      emit("onClickHeader", "filter", item.key);
    }
  };

  const handleSort = (item: DataTableHeader) => {
    const route = useRoute();
    const sorted = sortItem.value === item.key && sort.value === "desc" ? "asc" : "desc";
    const queries = filterEmptyObject({ ...route.query, sort: sorted, sort_item: item.key });
    historyStore.to({ query: queries });
  };

  const getHeaderClasses = (header: DataTableHeader) => {
    const classes: string[] = ["d-flex", "align-center"];
    classes.push(`text-${header.align || "center"}`);
    classes.push(`justify-${header.align || "center"}`);
    if (header.sortable || header.filterable) {
      classes.push("cursor-pointer");
    }
    return classes;
  };
</script>
<template>
  <div class="data-table">
    <template v-if="!isSp">
      <div class="columns header px-4" data-testid="data-table-header">
        <div
          v-for="header in headers"
          :key="header.key"
          :style="`width: ${header.width}%;`"
          @click="handleClickHeader(header)"
        >
          <div
            v-if="!header.hideTitlePc"
            :class="[
              getHeaderClasses(header).join(' '),
              { 'sorted-item': header.sortable && sortItem === header.key, 'sortable-item': header.sortable },
            ]"
            :data-testid="`data-table-header-${header.key}`"
          >
            <span class="header-label">
              {{ header.label }}
            </span>
            <div v-if="header.filterable" class="filterable">
              <v-icon
                :color="[...(selectedFilters || [])].includes(header.key) ? 'blue' : 'white'"
                icon="$mdiFilter"
                :size="16"
                class="filter"
              />
            </div>
            <div v-if="header.sortable" class="sortable">
              <v-icon :color="getSortActiveColor(header, 'asc')" icon="$mdiTriangleSmallUp" :size="24" class="up" />
              <v-icon
                :color="getSortActiveColor(header, 'desc')"
                icon="$mdiTriangleSmallDown"
                :size="24"
                class="down"
              />
            </div>
          </div>
        </div>
      </div>
      <v-sheet
        v-for="item in items"
        :key="item.id"
        class="data-table-item my-4 px-2 py-4"
        :class="[{ hoverable: hoverable }, `cursor-${rowCursor || 'default'}`]"
        color="monochrome-05"
        rounded
        data-testid="data-table-item"
      >
        <div class="columns">
          <template v-for="header in headers" :key="header.key">
            <component
              :is="
                () =>
                  h(
                    'div',
                    {
                      class: `text-value py-2 justify-${header.align || 'center'}`,
                      style: `width: ${header.width}%;`,
                      'data-testid': `data-table-item-${header.key}`,
                      onClick: () => header.clickable && emit('onClickItem', item),
                    },
                    header.render(item),
                  )
              "
            />
          </template>
        </div>
      </v-sheet>
    </template>
    <template v-else>
      <div v-for="item in items" :key="item.id" class="mx-0 my-4" data-testid="data-table-sp-item">
        <div v-for="(rows, index) in getSpRows()" :key="index" data-testid="data-table-sp-row">
          <v-sheet class="columns-sp pa-4" rounded color="monochrome-05">
            <div v-for="header in getSpHeaders(rows)" :key="header.key" :style="`width: ${header.spWidth}%;`">
              <div
                v-if="!header.hideTitleSp"
                :class="`text-title mb-1 text-${header.spAlign}`"
                :data-testid="`data-table-sp-header-${header.key}`"
              >
                {{ header.label }}
              </div>
              <component
                :is="
                  () =>
                    h(
                      'div',
                      {
                        class: `text-value-s justify-${header.spAlign}`,
                        'data-testid': `data-table-sp-item-${header.key}`,
                        onClick: () => header.clickable && emit('onClickItem', item),
                      },
                      header.render(item),
                    )
                "
              />
            </div>
          </v-sheet>
          <v-sheet
            v-if="(spRows.length != index + 1 && showDetail) || (spDefaultRows || 1) - 2 == index"
            color="monochrome-05 px-4"
          >
            <v-divider />
          </v-sheet>
        </div>
      </div>
    </template>
  </div>
</template>
<style lang="scss" scoped>
  .sortable {
    position: relative;
    display: inline-block;
    width: 24px;
    height: 24px;
    .up,
    .down {
      position: absolute;
      top: 0;
      left: 0;
    }
    .up {
      top: -3px;
    }
    .down {
      top: 6px;
    }
  }
  .filterable {
    position: relative;
    display: inline-block;
    width: 16px;
    height: 20px;
    .filter {
      position: absolute;
      top: 3px;
      left: 2px;
    }
  }
</style>
